import { Button, ComponentLoader, Span, StyledLink, Tooltip } from "@netapp/bxp-design-system-react";
import { InfoIcon } from "@netapp/bxp-style/react-icons/Notification";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { globalSearch as i18nNamespace} from "../../../i18n/namespace";
import ConsoleObjectResult, { highlightMatch } from "./ConsoleObjectResult";
import {
    CONSOLE_OBJECTS_PREVIEW_LIMIT,
    CONSOLE_OBJECTS_SEE_ALL_LIMIT,
    CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT,
    ONTAP_OBJECT_TYPE_FILTERS,
    getEmptyConsoleObjects
} from "../consoleObjects";
import { inputMethodFromInteraction } from "../globalSearchFullStory";
import { GLOBAL_SEARCH_FILTER_SLOT_ID } from "./Input";
import { ObjectTypeFilterControl, apiTypesForFilter, defaultObjectTypeFilter, normalizeObjectTypeFilter, objectTypesRequest, suppressGlobalSearchClose, useObjectTypeFilter } from "./objectTypeFilter";
import styles from "../GlobalSearch.module.scss";
const sectionCountLabel = (shown, total, t) => {
    const overall = typeof total === "number" ? total : shown;
    return shown < overall ? t('showing_count_of_total', { shown, total: overall }) : overall;
};
const withExactMatchesFirst = (items, searchTerm, labelOf) => {
    const term = typeof searchTerm === "string" ? searchTerm.trim().toLowerCase() : "";
    if (!term || !items?.length) {
        return items || [];
    }
    const exact = [];
    const rest = [];
    items.forEach(item => {
        const label = labelOf(item);
        if (typeof label === "string" && label.trim().toLowerCase() === term) {
            exact.push(item);
        } else {
            rest.push(item);
        }
    });
    return exact.length ? [...exact, ...rest] : items;
};

const HighlightedLabel = ({ label, searchTerm }) => highlightMatch(label, searchTerm);

const ReferenceRows = ({ results, searchTerm, selectType = "documentation" }) => {
    const dispatch = useDispatch();

    return results.map((result, index) => {
        const resultLabel = result.label || result.name;
        if (typeof resultLabel !== "string" || !resultLabel) {
            return null;
        }
        const key = `${result.id || resultLabel}-${index}`;
        const labelNode = <HighlightedLabel label={resultLabel} searchTerm={searchTerm} />;
        if (!result.to && !result.href) {
            return <span className={styles['reference-row']} key={key}>{labelNode}</span>;
        }
        const Component = result.to ? StyledLink : "a";
        const linkProps = result.to
            ? { to: result.to }
            : { href: result.href, target: "_blank", rel: "noreferrer" };
        return <Component
            {...linkProps}
            className={styles['reference-row']}
            key={key}
            onClick={event => {
                dispatch({
                    type: `GLOBAL-SEARCH/SELECT-SEARCH/${selectType}`,
                    payload: result,
                    track: resultLabel,
                    rank: index + 1,
                    inputMethod: inputMethodFromInteraction(event)
                });
                dispatch({ type: "GLOBAL-SEARCH/CLOSE" });
            }}
        >
            {labelNode}
        </Component>;
    });
};

const ReferenceSection = ({ label, results, count, searchTerm, expanded, loading, failed, onSeeAll, onClose, selectType }) => {
    const { t } = useTranslation(i18nNamespace);
    const orderedResults = withExactMatchesFirst(results, searchTerm, result => result.label || result.name);
    const shownResults = expanded ? orderedResults : orderedResults.slice(0, CONSOLE_OBJECTS_PREVIEW_LIMIT);
    const total = typeof count === "number" ? count : results.length;

    return <div>
        <div className={styles.header}>
            <span>{`${label} (${sectionCountLabel(shownResults.length, total, t)})`}</span>
            {!expanded && results.length > 0 && <Button type="text" disabled={loading} onMouseDown={e => e.preventDefault()} onClick={onSeeAll}>
                {t('see_all')}
            </Button>}
            {expanded && <Button type="text" disabled={loading} onMouseDown={e => e.preventDefault()} onClick={onClose}>
                {t('close')}
            </Button>}
        </div>
        <ReferenceRows results={shownResults} searchTerm={searchTerm} selectType={selectType} />
        {loading && <div className={styles.message}>
            <ComponentLoader className={styles['loading-icon']}/>
            <Span className={styles['loading-label']}>{t('loading_results')}</Span>
        </div>}
        {!loading && failed && <div className={`${styles.message} ${styles.centered}`}>{t('search_error_message')}</div>}
        {!loading && !failed && !results.length && <div className={`${styles.message} ${styles.centered}`}>{t('no_results_message')}</div>}
    </div>;
};

const showScopeTooltip = event => {
    event.currentTarget.parentElement?.dispatchEvent(new MouseEvent("mouseenter", { bubbles: true }));
};

const hideScopeTooltip = event => {
    event.currentTarget.parentElement?.dispatchEvent(new MouseEvent("mouseleave", { bubbles: true }));
};

const SearchScopeInfo = () => {
    const { t } = useTranslation(i18nNamespace);

    return <span className={styles['scope-info']}>
        <button
            type="button"
            className={styles['scope-info-button']}
            aria-label={t('search_scope')}
            onMouseDown={event => event.preventDefault()}
            onFocus={showScopeTooltip}
            onBlur={hideScopeTooltip}
        >
            <InfoIcon />
        </button>
        <Tooltip contentClassName={styles['scope-tooltip']}>
            {t('search_scope_tooltip')}
        </Tooltip>
    </span>;
};

const SearchResults = () => {
    const { t } = useTranslation(i18nNamespace);
    const dispatch = useDispatch();
    const filterContext = useObjectTypeFilter();
    const [localObjectTypeFilter, setLocalObjectTypeFilter] = useState(defaultObjectTypeFilter);
    const objectTypeFilter = filterContext?.objectTypeFilter ?? localObjectTypeFilter;
    const setObjectTypeFilter = filterContext?.setObjectTypeFilter ?? setLocalObjectTypeFilter;
    const [expandedSection, setExpandedSection] = useState(null);
    const searchTerm = useSelector(state => state.globalSearch.searchTerm);
    const consoleObjects = useSelector(state => state.globalSearch.consoleObjects) || getEmptyConsoleObjects();
    const documentationResults = useSelector(state => state.globalSearch.documentationResults) || {};
    const searchOperations = useSelector(state => state.operations?.byType["GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH"]);
    const seeAllOperations = useSelector(state => state.operations?.byType["GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL"]);
    const documentationOperations = useSelector(state => state.operations?.byType["GLOBAL-SEARCH/DOCUMENTATION/SEARCH"]);
    const knowledgeBaseOperations = useSelector(state => state.operations?.byType["GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH"]);
    const isOperationActive = operations => Array.isArray(operations) ? operations.length > 0 : !!operations;
    const isSearching = isOperationActive(searchOperations) || isOperationActive(seeAllOperations);
    const isDocumentationSearching = isOperationActive(documentationOperations);
    const isKnowledgeBaseSearching = isOperationActive(knowledgeBaseOperations);
    const isConsoleExpanded = expandedSection === "console";
    const selectedObjectTypes = ONTAP_OBJECT_TYPE_FILTERS.filter(item => objectTypeFilter[item.label]).map(item => item.label);
    const allObjectTypesSelected = selectedObjectTypes.length === ONTAP_OBJECT_TYPE_FILTERS.length;
    const orderedConsoleObjects = withExactMatchesFirst(
        allObjectTypesSelected
            ? consoleObjects.results
            : consoleObjects.results.filter(result => selectedObjectTypes.includes(result.objectType)),
        searchTerm,
        result => result.name
    );
    const shownConsoleObjects = isConsoleExpanded
        ? orderedConsoleObjects
        : orderedConsoleObjects.slice(0, CONSOLE_OBJECTS_PREVIEW_LIMIT);
    const showConsoleSeeAll = !isConsoleExpanded && shownConsoleObjects.length > 0;
    const [filterSlot, setFilterSlot] = useState(null);
    useLayoutEffect(() => {
        if (filterContext) {
            return;
        }
        setFilterSlot(document.getElementById(GLOBAL_SEARCH_FILTER_SLOT_ID));
    }, [filterContext]);
    if (filterContext?.consoleExpandedRef) {
        filterContext.consoleExpandedRef.current = isConsoleExpanded;
    }
    const countLabel = sectionCountLabel(shownConsoleObjects.length, consoleObjects.count, t);
    const knowledgeBaseResults = documentationResults["Knowledge Base"] || [];
    const documentation = documentationResults["NetApp Documentation"] || [];

    const expandedSectionRef = useRef(expandedSection);
    expandedSectionRef.current = expandedSection;
    const objectTypeFilterRef = useRef(objectTypeFilter);
    objectTypeFilterRef.current = objectTypeFilter;

    useEffect(() => {
        const section = expandedSectionRef.current;
        if (!section || !searchTerm) {
            return;
        }
        if (section === "console") {
            const objectTypes = apiTypesForFilter(objectTypeFilterRef.current);
            dispatch({
                type: "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL",
                payload: {
                    searchTerm,
                    limit: CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT,
                    ...objectTypesRequest(objectTypes)
                }
            });
            return;
        }
        dispatch({
            type: section === "knowledgeBase" ? "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH" : "GLOBAL-SEARCH/DOCUMENTATION/SEARCH",
            payload: {
                searchTerm,
                limit: CONSOLE_OBJECTS_SEE_ALL_LIMIT
            }
        });
    }, [searchTerm, dispatch]);

    const seeAllPayload = (objectTypes) => ({
        searchTerm,
        limit: CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT,
        ...objectTypesRequest(objectTypes)
    });

    const showAllConsoleObjects = () => {
        setExpandedSection("console");
        dispatch({
            type: "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL",
            payload: seeAllPayload(apiTypesForFilter(objectTypeFilter))
        });
    };

    const closeAllConsoleObjects = () => {
        setExpandedSection(null);
        setObjectTypeFilter(defaultObjectTypeFilter());
        dispatch({
            type: "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH",
            payload: {
                searchTerm,
                limit: CONSOLE_OBJECTS_PREVIEW_LIMIT
            }
        });
    };

    const showReferenceSection = (section, actionType) => {
        setExpandedSection(section);
        dispatch({
            type: actionType,
            payload: {
                searchTerm,
                limit: CONSOLE_OBJECTS_SEE_ALL_LIMIT
            }
        });
    };

    const closeReferenceSection = actionType => {
        setExpandedSection(null);
        dispatch({
            type: actionType,
            payload: {
                searchTerm,
                limit: CONSOLE_OBJECTS_PREVIEW_LIMIT
            }
        });
    };

    const updateObjectFilter = values => {
        suppressGlobalSearchClose();
        const nextFilter = normalizeObjectTypeFilter(values);
        const objectTypes = apiTypesForFilter(nextFilter);
        setObjectTypeFilter(nextFilter);
        if (!searchTerm) {
            return;
        }
        dispatch({
            type: isConsoleExpanded ? "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL" : "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH",
            payload: isConsoleExpanded
                ? seeAllPayload(objectTypes)
                : {
                    searchTerm,
                    limit: CONSOLE_OBJECTS_PREVIEW_LIMIT,
                    ...objectTypesRequest(objectTypes)
                }
        });
    };

    const objectFilter = <ObjectTypeFilterControl values={objectTypeFilter} onChange={updateObjectFilter} />;

    const showKnowledgeBase = expandedSection === null || expandedSection === "knowledgeBase";
    const showDocumentation = expandedSection === null || expandedSection === "documentation";
    const showConsoleObjects = expandedSection === null || expandedSection === "console";
    const consoleSearchFailed = !!consoleObjects.error;
    const failedTypeNames = (consoleObjects.failedObjectTypes || [])
        .map(apiType => ONTAP_OBJECT_TYPE_FILTERS.find(item => item.apiType === apiType))
        .filter(Boolean)
        .map(item => t(`object_types.${item.labelKey}`));
    const consolePartialError = !!consoleObjects.partialError && !consoleSearchFailed && failedTypeNames.length > 0;

    const filterNode = filterContext ? null : (filterSlot ? createPortal(objectFilter, filterSlot) : objectFilter);

    return <div>
        {filterNode}
        <div className={styles.list}>
            {showConsoleObjects && <div>
            <div className={styles.header}>
                <span>{`${t('console_objects')} (${countLabel})`}</span>
                <span className={styles['header-actions']}>
                    {showConsoleSeeAll && <Button type="text" disabled={isSearching} onMouseDown={e => e.preventDefault()} onClick={showAllConsoleObjects}>
                        {t('see_all')}
                    </Button>}
                    {isConsoleExpanded && <Button type="text" disabled={isSearching} onMouseDown={e => e.preventDefault()} onClick={closeAllConsoleObjects}>
                        {t('close')}
                    </Button>}
                </span>
            </div>
            {shownConsoleObjects.map((result, index) => (
                <ConsoleObjectResult key={`${result.id || result.name}-${index}`} {...result} rank={index + 1} />
            ))}
            {isSearching && <div className={styles.message}>
                <ComponentLoader className={styles['loading-icon']}/>
                <Span className={styles['loading-label']}>{t('loading_results')}</Span>
            </div>}
            {!isSearching && consoleSearchFailed && <div className={`${styles.message} ${styles.centered}`}>{t('search_error_message')}</div>}
            {!isSearching && consolePartialError && <div className={`${styles.message} ${styles.centered}`}>{t('search_partial_error_message', { types: failedTypeNames.join(', ') })}</div>}
            {!shownConsoleObjects.length && !isSearching && !consoleSearchFailed && <div className={`${styles.message} ${styles.centered}`}>{t('no_results_message')}</div>}
            </div>}
            {showKnowledgeBase && <ReferenceSection
                label={t('knowledge_base')}
                results={knowledgeBaseResults}
                count={documentationResults.counts?.["Knowledge Base"]}
                searchTerm={searchTerm}
                loading={isKnowledgeBaseSearching}
                failed={!!documentationResults.errors?.["Knowledge Base"]}
                expanded={expandedSection === "knowledgeBase"}
                selectType="knowledge-base"
                onSeeAll={() => showReferenceSection("knowledgeBase", "GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH")}
                onClose={() => closeReferenceSection("GLOBAL-SEARCH/KNOWLEDGE-BASE/SEARCH")}
            />}
            {showDocumentation && <ReferenceSection
                label={t('documentation')}
                results={documentation}
                count={documentationResults.counts?.["NetApp Documentation"]}
                searchTerm={searchTerm}
                loading={isDocumentationSearching}
                failed={!!documentationResults.errors?.["NetApp Documentation"]}
                expanded={expandedSection === "documentation"}
                onSeeAll={() => showReferenceSection("documentation", "GLOBAL-SEARCH/DOCUMENTATION/SEARCH")}
                onClose={() => closeReferenceSection("GLOBAL-SEARCH/DOCUMENTATION/SEARCH")}
            />}
        </div>
        <div className={styles.scope}>
            {t('search_scope')}
            <SearchScopeInfo />
        </div>
    </div>
}

export default SearchResults;
